import type { CurrentUser } from "./types";
import {
  LayoutDashboard, Database, Building2, Pencil, Files,
  Activity, ClipboardCheck, Send, FileText, Users, Network, Calendar, Calculator,
  type LucideIcon,
} from "lucide-react";
export const isNbs = (u: CurrentUser | null) => !!u && (u.is_nbs || u.is_superuser);
export const isMda = (u: CurrentUser | null) => !!u && (u.is_mda || u.is_superuser);
// A TRUE MDA user (not a superuser wearing the MDA hat). Used for own-agency-only nav
// like "My submissions", which a superuser doesn't need (they have All submissions).
export const isMdaOnly = (u: CurrentUser | null) => !!u && u.is_mda && !u.is_superuser;
const rolesOf = (u: CurrentUser | null): string[] =>
  u ? ((u.roles && u.roles.length) ? u.roles : (u.role ? [u.role] : [])) : [];
export const canPublish = (u: CurrentUser | null) => !!u && (u.is_superuser || rolesOf(u).includes("director"));
export const canValidate = (u: CurrentUser | null) =>
  !!u && (u.is_superuser || rolesOf(u).some((r) => ["nbs_validation", "nbs_data_admin", "director"].includes(r)));
export const canManageUsers = (u: CurrentUser | null) =>
  !!u && (u.is_superuser || rolesOf(u).includes("nbs_sys_admin"));
export const seesAllData = (u: CurrentUser | null) => isNbs(u); // MDA scoped to own agency
// The NBS Data Administrator loads data on behalf of agencies and may also type entries.
export const isDataAdmin = (u: CurrentUser | null) =>
  !!u && (u.is_superuser || rolesOf(u).includes("nbs_data_admin"));
// WHO MAY ENTER DATA: MDA focal/supervisor, superuser, and the NBS Data Administrator.
export const canEnterData = (u: CurrentUser | null) =>
  !!u && (u.is_superuser || u.is_mda || isDataAdmin(u));
export interface NavItem { to: string; label: string; icon: LucideIcon; show?: (u: CurrentUser | null) => boolean; }
export interface NavGroup { group: string; items: NavItem[]; show: (u: CurrentUser | null) => boolean; }
export const NAV: NavGroup[] = [
  { group: "Overview", show: () => true, items: [
    { to: "/", label: "National dashboard", icon: LayoutDashboard },
    { to: "/microdata", label: "Microdata & export", icon: Database },
  ]},
  // Group shows for true MDA users OR anyone who may enter data (data-admin, superuser).
  // Upload template has been removed. "My submissions" is own-agency, so it's shown to
  // true MDA users only - superusers use "All submissions" under NBS back office.
  { group: "My agency", show: (u) => isMda(u) || canEnterData(u), items: [
    { to: "/agency", label: "Agency dashboard", icon: Building2, show: isMda },
    { to: "/agency/entry", label: "Data entry", icon: Pencil, show: canEnterData },
    { to: "/agency/submissions", label: "My submissions", icon: Files, show: isMdaOnly },
  ]},
  { group: "NBS back office", show: (u) => isNbs(u), items: [
    { to: "/nbs", label: "Monitoring & executive", icon: Activity },
    { to: "/nbs/submissions", label: "All submissions", icon: Files },
    { to: "/nbs/validation", label: "Validation queue", icon: ClipboardCheck },
    { to: "/nbs/denominators", label: "Denominators & references", icon: Calculator },
    { to: "/nbs/publish", label: "Publishing", icon: Send },
    { to: "/nbs/audit", label: "Audit log", icon: FileText },
  ]},
  { group: "Administration", show: (u) => canManageUsers(u), items: [
    { to: "/admin/users", label: "Users", icon: Users },
    { to: "/admin/agencies", label: "Agencies", icon: Network },
    { to: "/admin/cycles", label: "Survey cycles", icon: Calendar },
  ]},
];
